import RevxFlexLayout from 'components/RevxFlexLayout';
import { Divider, Form, FormInstance, Input, Tooltip } from 'antd';
import {
  JobCostTypeEnum,
  totalColumns,
  TotalRow,
  TotalRowKeysEnum,
} from '../adjustmentTypes';
import classnames from 'classnames';
import {
  JobAdjustmentFee,
  JobAdjustmentParams,
  JobAdjustmentViewer,
  JobItem,
  JobParticipantEnum,
  JobTypeEnum,
} from 'domains/workspaceDomain';
import { useDebounceFn, useMemoizedFn } from 'ahooks';
import { isBuyer, isSupplier } from 'utils/rbac';
import { useUser } from 'hooks';
import { CloseIcon } from 'components/Icons';
import { DEBOUNCE_TIME, INPUT_NORMAL_MAX_LENGTH } from 'utils/constants';
import workspaceService from 'services/jobService';
import { RevxMoney } from 'components/RevxMoney';
import { RevxInputNumber } from 'components/RevxInputNumber';
import { useAppDispatch } from 'app/hooks';
import React from 'react';

type Props = {
  data: JobAdjustmentViewer;
  totalListForm: FormInstance;
  totalRowsState: TotalRow[];
  isOriginalPage: boolean;
  isReadjust: boolean;
  canEditStatusByRole: () => boolean;
  runDebounceUpdateJobAdjustmentCostDataHandler: (
    params: JobAdjustmentParams
  ) => void;
  updateJobAdjustmentData: (data: JobAdjustmentViewer) => void;
  generateJobAdjustmentCopy: (category?: boolean) => void;
  jobData?: JobItem;
};

const JobAdjustmentTotal = function ({
  data,
  totalListForm,
  totalRowsState,
  jobData,
  isOriginalPage,
  isReadjust,
  canEditStatusByRole,
  runDebounceUpdateJobAdjustmentCostDataHandler,
  updateJobAdjustmentData,
  generateJobAdjustmentCopy,
}: Props) {
  const { originalTotal, adjustTotal } = data;
  const dispatch = useAppDispatch();
  const user = useUser();
  const { CUSTOMER, PRODUCER, OPERATOR, PROXY } = JobParticipantEnum;
  const isInternalJob = jobData?.jobType === JobTypeEnum.INTERNAL;
  const runDebounceUpdateFeeRowDataHandler = async (
    params: JobAdjustmentFee
  ) => {
    cancelUpdateFeeRowData();
    await updateFeeRowDataHandler(params);
  };
  const { run: updateFeeRowDataHandler, cancel: cancelUpdateFeeRowData } =
    useDebounceFn(
      async (params: JobAdjustmentFee) => {
        await updateFeeRowData(params);
      },
      {
        wait: DEBOUNCE_TIME,
      }
    );
  const updateFeeRowData = async (params: JobAdjustmentFee) => {
    if (!canEditStatusByRole()) {
      return;
    }
    const v = await workspaceService.updateJobAdjustmentFeeRow(
      data.jobId,
      data.jobAdjustmentId,
      params
    );
    updateJobAdjustmentData(v);
  };
  const hideMarkupByBuyer = useMemoizedFn((key) => {
    return (
      isBuyer(user) &&
      [TotalRowKeysEnum.jobMarkup, TotalRowKeysEnum.shippingMarkup].includes(
        key as TotalRowKeysEnum
      )
    );
  });
  const renderTotalDom = (
    index: number,
    { label, key, serialNumber }: TotalRow
  ) => {
    const { fee } = TotalRowKeysEnum;
    switch (index) {
      case 0:
        const m = () => {
          if (label) {
            if (key === fee) {
              return (
                <div
                  className="fee-btn-wrap"
                  onClick={async () => {
                    await addFeeRow();
                  }}
                >
                  {label}
                </div>
              );
            }
            return label;
          } else {
            const defaultDesValue =
              totalRowsState[findTotalFeeRowIndex(key)].description;
            return (
              <RevxFlexLayout className={`fee-wrap`}>
                <span
                  onClick={async () => {
                    await delFeeRow(serialNumber || 0);
                  }}
                >
                  {canEditStatusByRole() && <CloseIcon className="close" />}
                </span>
                <Form.Item
                  name={totalRowsState[findTotalFeeRowIndex(key)].key}
                  rules={[{ required: true, whitespace: true }]}
                >
                  {!canEditStatusByRole() ? (
                    <div className="fee-des">
                      <Tooltip title={defaultDesValue}>
                        {defaultDesValue}
                      </Tooltip>
                    </div>
                  ) : (
                    <Input
                      placeholder="Describe fee"
                      maxLength={INPUT_NORMAL_MAX_LENGTH}
                      defaultValue={defaultDesValue}
                      onChange={async (e) => {
                        const v = e.target.value;
                        if (v) {
                          await runDebounceUpdateFeeRowDataHandler({
                            serialNumber,
                            description: e.target.value,
                          });
                        }
                      }}
                    />
                  )}
                </Form.Item>
              </RevxFlexLayout>
            );
          }
        };
        return <div>{m()}</div>;
      case 1:
        return <div>{renderTotalCell(JobCostTypeEnum.originalTotal, key)}</div>;
      case 2:
        return <div>{renderTotalCell(JobCostTypeEnum.adjustTotal, key)}</div>;
    }
  };
  const renderTotalCell = (
    type: JobCostTypeEnum,
    key: TotalRowKeysEnum | string
  ) => {
    if (isOriginalPage && type === JobCostTypeEnum.adjustTotal && !isReadjust) {
      return <div>TBD</div>;
    }
    const {
      printing,
      shipping,
      shippingMarkup,
      fee,
      jobMarkup,
      taxes,
      totalCharges,
    } = TotalRowKeysEnum;
    const isOriginal = type === JobCostTypeEnum.originalTotal;
    switch (key) {
      case printing:
        return (
          <div>
            <RevxMoney value={data[type].printingCost} />
          </div>
        );
      case shipping:
        return (
          <div>
            <RevxMoney value={data[type].shippingCost} />
          </div>
        );
      case shippingMarkup:
        if (isOriginal) {
          return (
            <RevxFlexLayout>
              <span>{originalTotal.shippingMarkupPct}%</span>
              <Divider type="vertical" />
              <span>
                <RevxMoney value={originalTotal.shippingMarkup} />
              </span>
            </RevxFlexLayout>
          );
        }
        return (
          <RevxFlexLayout className={`markup-wrap`}>
            <RevxInputNumber
              disabled={!canEditStatusByRole()}
              value={adjustTotal.shippingMarkupPct}
              suffix="%"
              precision={2}
              onChange={async (shippingMarkupPct) => {
                const shippingMarkup = Number(
                  (
                    adjustTotal.shippingCost *
                    (shippingMarkupPct / 100)
                  ).toFixed(2)
                );
                await runDebounceUpdateJobAdjustmentCostDataHandler({
                  shippingMarkupPct,
                  shippingMarkup,
                });
              }}
            />
            <Divider type="vertical" />
            <RevxInputNumber
              disabled={!canEditStatusByRole()}
              value={adjustTotal.shippingMarkup}
              prefix="$"
              precision={2}
              onChange={async (shippingMarkup) => {
                const shippingMarkupPct =
                  Number(
                    (shippingMarkup / adjustTotal.shippingCost).toFixed(2)
                  ) * 100;
                await runDebounceUpdateJobAdjustmentCostDataHandler({
                  shippingMarkupPct,
                  shippingMarkup,
                });
              }}
            />
          </RevxFlexLayout>
        );
      case fee:
        return <></>;
      case jobMarkup:
        if (isOriginal) {
          return (
            <RevxFlexLayout>
              <span>{originalTotal.printingMarkupPct}%</span>
              <Divider type="vertical" />
              <span>
                <RevxMoney value={originalTotal.printingMarkup} />
              </span>
            </RevxFlexLayout>
          );
        }
        return (
          <RevxFlexLayout className={`markup-wrap`}>
            <RevxInputNumber
              disabled={!canEditStatusByRole()}
              value={adjustTotal.printingMarkupPct}
              suffix="%"
              max={99.99}
              precision={2}
              onChange={async (printingMarkupPct) => {
                const printingMarkup = Number(
                  (
                    (adjustTotal.printingCost * (printingMarkupPct / 100)) /
                    ((100 - printingMarkupPct) / 100)
                  ).toFixed(2)
                );
                await runDebounceUpdateJobAdjustmentCostDataHandler({
                  printingMarkupPct,
                  printingMarkup,
                });
              }}
            />
            <Divider type="vertical" />
            <RevxInputNumber
              disabled={!canEditStatusByRole()}
              value={adjustTotal.printingMarkup}
              prefix="$"
              precision={2}
              onChange={async (printingMarkup) => {
                const printingMarkupPct = Number(
                  (
                    (printingMarkup /
                      (adjustTotal.printingCost + printingMarkup)) *
                    100
                  ).toFixed(2)
                );
                await runDebounceUpdateJobAdjustmentCostDataHandler({
                  printingMarkupPct,
                  printingMarkup,
                });
              }}
            />
          </RevxFlexLayout>
        );
      case taxes:
        return (
          <div>
            <RevxMoney value={data[type].tax} />
          </div>
        );
      case totalCharges:
        return (
          <div>
            {isSupplier(user) ? (
              <RevxMoney value={data[type].totalCost} />
            ) : (
              <RevxMoney value={data[type].totalCharge} />
            )}
          </div>
        );
      default:
        if (isOriginal) {
          return <></>;
        }
        const curFeeRow = totalRowsState[findTotalFeeRowIndex(key)];
        return (
          <RevxInputNumber
            disabled={!canEditStatusByRole()}
            prefix="$"
            precision={2}
            value={curFeeRow.fee}
            onChange={async (fee) => {
              await runDebounceUpdateFeeRowDataHandler({
                serialNumber: curFeeRow.serialNumber,
                fee,
              });
            }}
          />
        );
    }
  };

  const delFeeRow = async (serialNumber: number) => {
    if (!canEditStatusByRole()) {
      return;
    }
    await dispatch.app.showLoading();
    const v = await workspaceService.deleteJobAdjustmentFeeRow(
      data.jobId,
      data.jobAdjustmentId,
      {
        serialNumber,
      }
    );
    updateJobAdjustmentData(v);
    await dispatch.app.hideLoading();
  };
  const findTotalFeeRowIndex = (
    otherFeeKey?: string,
    curTotalRows?: TotalRow[]
  ) => {
    let k = 0;
    (curTotalRows || totalRowsState).forEach(({ key }, index) => {
      if (otherFeeKey) {
        if (otherFeeKey === key) k = index;
      } else {
        if (key === TotalRowKeysEnum.fee) {
          k = index;
        }
      }
    });
    return k;
  };
  const addFeeRow = async () => {
    if (!canEditStatusByRole()) {
      await generateJobAdjustmentCopy();
      return;
    }
    await dispatch.app.showLoading();
    const v = await workspaceService.addJobAdjustmentFeeRow(
      data.jobId,
      data.jobAdjustmentId,
      { fee: 0, description: '' }
    );
    updateJobAdjustmentData(v);
    await dispatch.app.hideLoading();
  };
  return (
    <>
      <RevxFlexLayout
        className="title-bar"
        justify="space-between"
        align="center"
      >
        <div className="title-bar__name">
          <span>Total</span>
        </div>
      </RevxFlexLayout>
      <RevxFlexLayout className="total-block">
        <RevxFlexLayout className="total-list">
          <Form form={totalListForm}>
            {totalColumns.map(({ label, width }, index) => {
              return (
                <div
                  className={classnames('total-col', {
                    'first-col': index === 0,
                  })}
                  key={index}
                  style={{ width }}
                >
                  <RevxFlexLayout className="total-col__title">
                    <span>{label}</span>
                  </RevxFlexLayout>
                  {totalRowsState.map((rowItem, totalRowIndex) => {
                    const frag = <React.Fragment key={totalRowIndex} />;
                    const { key } = rowItem;
                    const { fee, taxes, shippingMarkup, jobMarkup } =
                      TotalRowKeysEnum;
                    let hasCustomer = false,
                      hasProducer = false,
                      hasOperator = false;
                    jobData?.participants.forEach((i) => {
                      if (i === CUSTOMER || i === PROXY) {
                        hasCustomer = true;
                      } else if (i === PRODUCER) {
                        hasProducer = true;
                      } else if (i === OPERATOR) {
                        hasOperator = true;
                      }
                    });
                    if (
                      isInternalJob &&
                      (key === jobMarkup ||
                        key === shippingMarkup ||
                        key === fee ||
                        key === taxes)
                    ) {
                      return frag;
                    } else {
                      if (key === jobMarkup || key === shippingMarkup) {
                        if ((hasCustomer || hasProducer) && !hasOperator) {
                          return frag;
                        }
                      }
                      if ((key === fee || key === taxes) && hasProducer) {
                        return frag;
                      }
                      if (key === fee && !canEditStatusByRole()) {
                        return frag;
                      }
                    }
                    return (
                      <RevxFlexLayout
                        hidden={hideMarkupByBuyer(key)}
                        className={classnames('total-col__main', {
                          'fee-td': key === TotalRowKeysEnum.fee,
                          'total-td': key === TotalRowKeysEnum.totalCharges,
                        })}
                        key={key}
                      >
                        {renderTotalDom(index, rowItem)}
                      </RevxFlexLayout>
                    );
                  })}
                </div>
              );
            })}
          </Form>
        </RevxFlexLayout>
      </RevxFlexLayout>
    </>
  );
};

export default JobAdjustmentTotal;
